import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Home, Check, Clock, Lock, X, AlertTriangle } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { debugLog, debugLogError, startDebugSession } from '../lib/debugLogger'

// Every React Query cache key that depends on household_id, user row, or
// household-scoped data. After a user accepts an invite their household_id
// changes server-side; we must force every dependent query to refetch so
// the UI reflects the new household. Order doesn't matter — invalidate
// fires async refetches in parallel.
const HOUSEHOLD_SCOPED_KEYS: string[][] = [
  ['householdId'],
  ['userRow'],
  ['user-onboarding'],
  ['household'],
  ['householdMembers'],
  ['household-schedule'],
  ['pantry-items'],
  ['confirmations'],
  ['latestListMeta'],
  ['listItems'],
  ['recipe-suggestions'],
]

function invalidateHouseholdScope(qc: ReturnType<typeof useQueryClient>) {
  for (const key of HOUSEHOLD_SCOPED_KEYS) {
    qc.invalidateQueries({ queryKey: key })
  }
}

type State = 'loading' | 'valid' | 'invalid' | 'expired' | 'used' | 'joining' | 'done' | 'already_member'

export default function JoinHousehold() {
  const { token }     = useParams<{ token: string }>()
  const navigate      = useNavigate()
  const { user }      = useAuth()
  const queryClient   = useQueryClient()
  const [state,    setState]    = useState<State>('loading')
  const [error,    setError]    = useState('')
  const [inviteHouseholdId, setInviteHouseholdId] = useState<string | null>(null)

  useEffect(() => {
    if (!token) { setState('invalid'); return }
    validateToken()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  async function validateToken() {
    try {
      const { data: invite, error } = await supabase
        .from('household_invites')
        .select('household_id, expires_at, used')
        .eq('token', token!)
        .maybeSingle()

      if (error || !invite) { setState('invalid'); return }
      if (invite.used)       { setState('used');    return }
      if (new Date(invite.expires_at) < new Date()) { setState('expired'); return }

      setInviteHouseholdId(invite.household_id)
      setState('valid')
    } catch {
      setState('invalid')
    }
  }

  async function handleJoin() {
    if (!inviteHouseholdId) return

    if (!user) {
      localStorage.setItem('pending_invite_token', token!)
      navigate('/sign-in?invite=' + token)
      return
    }

    setState('joining')
    startDebugSession('household-invite-accept')
    void debugLog({
      feature: 'household',
      step:    '1-accept-start',
      payload: { invite_household_id: inviteHouseholdId, user_id: user.id },
    })
    try {
      const { data: currentUser } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', user.id)
        .single()

      if (currentUser?.household_id === inviteHouseholdId) {
        void debugLog({ feature: 'household', step: '2-already-member' })

        // The DB already reflects membership — but local React Query
        // caches were rendered against the PRE-merge state (e.g. user
        // was merged via signup-with-invite-token, or via an earlier
        // tap of the link in another session). The Household tab keeps
        // rendering the stale solo state until something invalidates.
        // Run the same cache-seed + invalidation that the success
        // path runs so the UI snaps to truth even on this branch.
        queryClient.setQueryData(['householdId', user.id], inviteHouseholdId)
        const { data: refreshedUser } = await supabase
          .from('users')
          .select('id, household_id, display_name, email, restock_notifications_enabled')
          .eq('id', user.id)
          .single()
        if (refreshedUser) {
          queryClient.setQueryData(['userRow', user.id], refreshedUser)
        }
        invalidateHouseholdScope(queryClient)
        void debugLog({ feature: 'household', step: '2a-already-member-cache-refreshed' })

        setState('already_member')
        return
      }

      // Data-loss confirmation: if the user has any pantry items, saved
      // recipes, or shopping lists in their CURRENT household, accepting
      // this invite moves them out and orphans that data. It stays with
      // their old household — surviving members keep using it — but the
      // user themselves loses access. Worse: if they're the only member
      // of their old household, the data has nobody to access it.
      const oldHouseholdId = currentUser?.household_id
      if (oldHouseholdId) {
        const [pantryRes, recipeRes, listRes] = await Promise.all([
          supabase.from('pantry_items')   .select('id', { count: 'exact', head: true }).eq('household_id', oldHouseholdId),
          supabase.from('saved_recipes')  .select('id', { count: 'exact', head: true }).eq('household_id', oldHouseholdId),
          supabase.from('shopping_lists') .select('id', { count: 'exact', head: true }).eq('household_id', oldHouseholdId),
        ])

        const pantryCount = pantryRes.count ?? 0
        const recipeCount = recipeRes.count ?? 0
        const listCount   = listRes.count   ?? 0
        const hasExistingData = pantryCount > 0 || recipeCount > 0 || listCount > 0

        void debugLog({
          feature: 'household',
          step:    '1a-existing-data-check',
          payload: { pantryCount, recipeCount, listCount, hasExistingData },
        })

        if (hasExistingData) {
          // Sole-member status changes the framing: the data is effectively
          // unreachable rather than just shared with others.
          const { count: oldMemberCount } = await supabase
            .from('users')
            .select('id', { count: 'exact', head: true })
            .eq('household_id', oldHouseholdId)
          const isSoleMember = (oldMemberCount ?? 0) === 1

          const summary = [
            pantryCount > 0 && `${pantryCount} pantry item${pantryCount === 1 ? '' : 's'}`,
            recipeCount > 0 && `${recipeCount} saved recipe${recipeCount === 1 ? '' : 's'}`,
            listCount   > 0 && `${listCount} shopping list${listCount   === 1 ? '' : 's'}`,
          ].filter(Boolean).join(', ')

          const confirmMsg = isSoleMember
            ? `Joining this household will move you out of your current household. Your data (${summary}) will stay with your old household, but since you're the only member there, you won't be able to access it anymore.\n\nContinue anyway?`
            : `Joining this household will move you out of your current household. Your data (${summary}) will stay with your current household and the other members there.\n\nContinue?`

          const confirmed = confirm(confirmMsg)
          if (!confirmed) {
            void debugLog({ feature: 'household', step: '1b-user-cancelled' })
            setState('valid')
            return
          }
          void debugLog({ feature: 'household', step: '1c-user-confirmed' })
        }
      }

      const { error: updateError } = await supabase
        .from('users')
        .update({ household_id: inviteHouseholdId })
        .eq('id', user.id)
      if (updateError) throw updateError
      void debugLog({
        feature: 'household',
        step:    '2-user-household-updated',
        payload: { from: currentUser?.household_id, to: inviteHouseholdId },
      })

      await supabase
        .from('household_invites')
        .update({ used: true, used_by: user.id, used_at: new Date().toISOString() })
        .eq('token', token!)
      void debugLog({ feature: 'household', step: '3-invite-marked-used' })

      // Push the new household_id straight into the React Query caches that
      // dependent queries use as their `enabled` gate. Without this,
      // `useHouseholdId` and `Household.tsx`'s userRow query would still
      // return the old value until they refetch — which can be after
      // /home is already mid-render with the wrong context.
      queryClient.setQueryData(['householdId', user.id], inviteHouseholdId)
      const { data: refreshedUser } = await supabase
        .from('users')
        .select('id, household_id, display_name, email, restock_notifications_enabled')
        .eq('id', user.id)
        .single()
      if (refreshedUser) {
        queryClient.setQueryData(['userRow', user.id], refreshedUser)
      }
      void debugLog({
        feature: 'household',
        step:    '4-cache-seeded',
        payload: { newHouseholdId: refreshedUser?.household_id ?? null },
      })

      // Force a refetch of every household-scoped query so members,
      // pantry, schedule, and lists all snap to the new household.
      invalidateHouseholdScope(queryClient)
      void debugLog({ feature: 'household', step: '5-cache-invalidated' })

      setState('done')
      setTimeout(() => {
        void debugLog({ feature: 'household', step: '6-navigating-home' })
        navigate('/home')
      }, 2000)
    } catch (err: any) {
      void debugLogError('household', 'accept', err)
      setError(err.message || 'Something went wrong')
      setState('valid')
    }
  }

  // ── Shared shell ──────────────────────────────────────────────────────────

  function Tile({
    Icon, tone = 'forest',
  }: { Icon: typeof Home; tone?: 'forest' | 'critical' | 'sand' }) {
    const ring = tone === 'critical' ? 'border-terracotta/30' :
                 tone === 'sand'     ? 'border-sand/40'       :
                                       'border-border-sand'
    const fg   = tone === 'critical' ? 'text-terracotta' :
                 tone === 'sand'     ? 'text-sand'       :
                                       'text-forest'
    return (
      <div className={`h-16 w-16 rounded-2xl border ${ring} bg-paper flex items-center justify-center mx-auto mb-6`}>
        <Icon className={`h-7 w-7 ${fg}`} strokeWidth={1.5} />
      </div>
    )
  }

  function Eyebrow({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'muted' | 'critical' | 'sand' }) {
    const tw = tone === 'critical' ? 'text-terracotta' :
               tone === 'sand'     ? 'text-sand' :
                                     'text-ink-muted'
    return (
      <p className={`text-[10px] uppercase tracking-[0.18em] font-medium mb-3 ${tw}`}>
        {children}
      </p>
    )
  }

  // ── Per-state content ─────────────────────────────────────────────────────

  const screens: Record<State, React.ReactNode> = {
    loading: (
      <>
        <Tile Icon={Home} />
        <span className="h-7 w-7 mx-auto block rounded-full border-2 border-forest border-t-transparent animate-spin mb-4" />
        <p className="font-serif italic text-[13px] text-ink-muted">Checking invite link…</p>
      </>
    ),
    valid: (
      <>
        <Tile Icon={Home} />
        <Eyebrow>You're invited</Eyebrow>
        <h1 className="font-serif text-[24px] text-ink leading-[1.15] tracking-[-0.015em] mb-2">
          Join a shared <em className="italic">pantry</em>
        </h1>
        <p className="font-serif italic text-[14px] text-ink-muted leading-[1.5] mb-7">
          Share groceries, save together.
        </p>
        {error && (
          <div className="rounded-xl bg-cream-warm border border-terracotta/30 px-4 py-2.5 mb-4 flex items-start gap-2.5 text-left">
            <AlertTriangle className="h-3.5 w-3.5 text-terracotta flex-shrink-0 mt-0.5" strokeWidth={1.8} />
            <p className="font-serif italic text-[12px] text-terracotta leading-[1.4]">{error}</p>
          </div>
        )}
        <button
          type="button"
          onClick={handleJoin}
          className="w-full rounded-2xl bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em] mb-3"
        >
          {user ? 'Join household' : 'Sign in to join'}
        </button>
        {!user && (
          <p className="font-serif italic text-[12px] text-ink-muted">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={() => {
                localStorage.setItem('pending_invite_token', token!)
                navigate('/sign-up?invite=' + token)
              }}
              className="text-forest font-medium not-italic underline-offset-2 underline decoration-forest/30"
            >
              Sign up
            </button>
          </p>
        )}
      </>
    ),
    joining: (
      <>
        <Tile Icon={Home} />
        <span className="h-7 w-7 mx-auto block rounded-full border-2 border-forest border-t-transparent animate-spin mb-4" />
        <p className="font-serif italic text-[13px] text-ink-muted">Joining household…</p>
      </>
    ),
    done: (
      <>
        <div className="h-16 w-16 rounded-2xl bg-ok-bg border border-forest/30 flex items-center justify-center mx-auto mb-6">
          <Check className="h-7 w-7 text-forest" strokeWidth={2} />
        </div>
        <Eyebrow>Welcome</Eyebrow>
        <h1 className="font-serif text-[24px] text-ink leading-[1.15] tracking-[-0.015em] mb-2">
          You're <em className="italic">in</em>
        </h1>
        <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5]">
          Taking you to your shared pantry…
        </p>
      </>
    ),
    already_member: (
      <>
        <div className="h-16 w-16 rounded-2xl bg-ok-bg border border-forest/30 flex items-center justify-center mx-auto mb-6">
          <Check className="h-7 w-7 text-forest" strokeWidth={2} />
        </div>
        <Eyebrow>Already a member</Eyebrow>
        <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mb-2">
          You're already <em className="italic">in</em>
        </h1>
        <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5] mb-6">
          You're already part of this household.
        </p>
        <button
          type="button"
          onClick={() => navigate('/home')}
          className="w-full rounded-2xl bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em]"
        >
          Go to pantry
        </button>
      </>
    ),
    expired: (
      <>
        <Tile Icon={Clock} tone="sand" />
        <Eyebrow tone="sand">Invite</Eyebrow>
        <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mb-2">
          Link <em className="italic">expired</em>
        </h1>
        <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5] mb-6">
          Ask the household owner to generate a new invite link.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sign-in')}
          className="w-full rounded-2xl bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em]"
        >
          Go to sign in
        </button>
      </>
    ),
    used: (
      <>
        <Tile Icon={Lock} tone="sand" />
        <Eyebrow tone="sand">Invite</Eyebrow>
        <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mb-2">
          Already <em className="italic">used</em>
        </h1>
        <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5] mb-6">
          This invite link has already been used. Ask for a new one.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sign-in')}
          className="w-full rounded-2xl bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em]"
        >
          Go to sign in
        </button>
      </>
    ),
    invalid: (
      <>
        <Tile Icon={X} tone="critical" />
        <Eyebrow tone="critical">Invite</Eyebrow>
        <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mb-2">
          Invalid <em className="italic">link</em>
        </h1>
        <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5] mb-6">
          This invite link isn't valid. Check the link and try again.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sign-in')}
          className="w-full rounded-2xl bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em]"
        >
          Go to sign in
        </button>
      </>
    ),
  }

  return (
    <main className="min-h-screen bg-cream flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-[400px] text-center">
        {screens[state]}
      </div>
    </main>
  )
}
